export type Options = Record<string, unknown>;

export const RoleType = {
  Explicit: 'Explicit',
  Inherited: 'Inherited',
} as const;
export type RoleType = (typeof RoleType)[keyof typeof RoleType];

export type Role = {
  code: RoleCode;
  name: string;
  roleType?: RoleType;
};

export type Persona = {
  id: string;
  userId: string;
  roles: Role[];
  credentialId?: string;
  instanceId: number;
  companyId?: number;
  serviceProviderId?: number | null;
  options?: Options;
  email?: string;
  firstName?: string;
  lastName?: string;
  linkedWorkerId?: string;
  isDisabled: boolean;
};

export type ServiceProvider = {
  id: number;
  name: string;
  options?: Options;
  countryCode?: string;
};

export type Instance = {
  id: number;
  name: string;
  modules: Module[];
  options?: Record<string, unknown>;
  countryCode?: string;
};

export type Company = {
  id: number;
  name: string;
  logoUrl?: string | null;
  avatarUrl?: string | null;
  isDisabled?: boolean;
  countryCode?: string;
  options?: Options;
};

/** DS V2 company read model — the shape returned by `GET /api/v2/companies`. */
export type CompanyApiModel = {
  id: number;
  name: string;
  instanceId?: number | null;
  serviceProviders: number[];
  parentCompanyId?: number | null;
  logoUrl?: string | null;
  options?: Options;
  avatarUrl?: string | null;
  isDisabled: boolean;
  countryCode: string;
};

/** DS V2 service provider read model — returned by `GET /api/v2/service-providers`. */
export type ServiceProviderApiModel = {
  id: number;
  name: string;
  logoUrl?: string | null;
  options?: Options;
  isDisabled: boolean;
  countryCode?: string | null;
};

export const ModuleStatus = {
  New: 'New',
  Available: 'Available',
  Unavailable: 'Unavailable',
  Creating: 'Creating',
  Updating: 'Updating',
  Archiving: 'Archiving',
  UpdateQueued: 'UpdateQueued',
} as const;

export type ModuleStatus = (typeof ModuleStatus)[keyof typeof ModuleStatus];

export type Module = {
  moduleType: ModuleType;
  moduleStatus?: ModuleStatus;
  url: string;
};

export const ModuleType = {
  None: 'None',
  HR: 'HR',
  GP: 'GP',
  Hire: 'Hire',
  Timesheets: 'Timesheets',
  Migration: 'Migration',
  Connect: 'Connect',
} as const;
export type ModuleType = (typeof ModuleType)[keyof typeof ModuleType];

export const RoleCode = {
  Administrator: 'Administrator',
  ClientAdmin: 'ClientAdmin',
  ClientUser: 'ClientUser',
  CompanyAccountant: 'CompanyAccountant',
  CompanyAdmin: 'CompanyAdmin',
  GlobalAdmin: 'GlobalAdmin',
  HireAdministrator: 'HireAdministrator',
  HireCompanyAdmin: 'HireCompanyAdmin',
  HireManager: 'HireManager',
  HrCompanyAdmin: 'HrCompanyAdmin',
  HRManager: 'HRManager',
  Manager: 'Manager',
  PayrollAdministrator: 'PayrollAdministrator',
  PayrollCompanyAccountant: 'PayrollCompanyAccountant',
  PayrollCompanyAdmin: 'PayrollCompanyAdmin',
  PayrollCompanyEmployee: 'PayrollCompanyEmployee',
  PayrollManager: 'PayrollManager',
  PSPAdmin: 'PSPAdmin',
  Recruiter: 'Recruiter',
  TimesheetApprover: 'TimesheetApprover',
  Worker: 'Worker',
} as const;
export type RoleCode = (typeof RoleCode)[keyof typeof RoleCode];

type BaseUser = {
  id?: string;
  firstName: string | null;
  lastName: string | null;
  /** @deprecated Use email instead */
  primaryEmail?: string | null;
  email?: string | null;
  /** @deprecated Use profilePhotoUrl instead */
  profilePhoto?: string | null;
  profilePhotoUrl?: string | null;
  phone?: string | null;
};

export type User = BaseUser & {
  /** @deprecated */
  options?: Options;
};

export type Identity = BaseUser & {
  /** @deprecated Use phone instead */
  phoneNumber?: {
    alias: string;
    type: string;
    countryCode: string;
    areaCode: string;
    phoneNumber: string;
    default: true;
  }[];

  /** @deprecated Use options instead */
  mfa?: {
    status: string;
    phoneNumber: {
      countryCode: string;
      areaCode: string;
      phoneNumber: string;
    };
  };
  /** @deprecated Use pohone instead */
  phoneNumbers?: {
    default: boolean;
    phoneNumber: string;
  }[];
  createdDateTime: Date;
  activationDateTime?: Date;
  options: IdentityOption;
};

export type IdentityOption = {
  sms: boolean;
  deviceEnrollment: boolean;
  totp: boolean;
};

export interface UpdateUserIdentity {
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
}

export type EventLogs = {
  pageNumber: number;
  pageSize: number;
  total: number;
  eventLogs: UserLoginEvent[];
};

export type UserLoginEvent = {
  id: string;
  userId: string;
  eventDateTime: Date;
  eventType: string;
  ipAddress: string;
  browser: string;
  os: string;
  additionalDetails: string; //TODO: Confirm this json string shape
};

export type PersonaTransfer = {
  id: string;
  personaIds: string[];
  toEmail: string;
  status: TransferStatus;
  expiryDateTime: string;
  hasExpired: boolean;
};

export const TransferStatus = {
  Pending: 'Pending',
  Verifying: 'Verifying',
  Verified: 'Verified',
  Canceled: 'Canceled',
} as const;
export type TransferStatus = (typeof TransferStatus)[keyof typeof TransferStatus];
